import React from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { REBRAND_COLOR } from '@latitude/core/utils/constants';
import Typography from '@latitude/typography';
import { getCdrEntryBindingRichTextOptions } from '@/utils/contentfulRichTextBindings';

const FAQ_RICH_TEXT_CONFIG = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => (
      <Typography.Body
        color={REBRAND_COLOR.BLACK_80}
        css={{ minHeight: '1.5em', marginBottom: '16px' }}
      >
        {children}
      </Typography.Body>
    )
  }
};

const ACCORDION_RICH_TEXT_CONFIG = getCdrEntryBindingRichTextOptions({
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => <>{children}</>,
    [BLOCKS.HEADING_6]: (node, children) => <>{children}</>
  }
});

const getNodeText = node => {
  if (!node) return '';

  if (node.nodeType === 'text') {
    return node.value || '';
  }

  if (Array.isArray(node.content)) {
    return node.content.map(getNodeText).join('');
  }

  return '';
};

const hasInlineEmbeddedEntry = node => {
  if (!node) return false;

  if (node.nodeType === INLINES.EMBEDDED_ENTRY) {
    return true;
  }

  return Array.isArray(node.content)
    ? node.content.some(hasInlineEmbeddedEntry)
    : false;
};

const renderRichTextBlock = node =>
  documentToReactComponents(
    {
      nodeType: BLOCKS.DOCUMENT,
      data: {},
      content: [node]
    },
    ACCORDION_RICH_TEXT_CONFIG
  );

const getBlockContent = node =>
  hasInlineEmbeddedEntry(node)
    ? renderRichTextBlock(node)
    : getNodeText(node).trim();

const getRichTextListItems = richTextDoc => {
  if (!richTextDoc?.content) {
    return [];
  }

  const unorderedListNode = richTextDoc.content.find(
    node => node.nodeType === BLOCKS.UL_LIST
  );

  if (!unorderedListNode?.content) {
    return [];
  }

  return unorderedListNode.content.map(getBlockContent).filter(Boolean);
};

const getRichTextParagraphContent = richTextDoc => {
  if (!richTextDoc?.content) {
    return '';
  }

  const paragraphNode = richTextDoc.content.find(
    node => node.nodeType === BLOCKS.PARAGRAPH && getBlockContent(node)
  );

  return paragraphNode ? getBlockContent(paragraphNode) : '';
};

const mapAccordionContent = richTextDoc => {
  if (!richTextDoc?.content) {
    return [];
  }

  return richTextDoc.content.reduce((acc, node) => {
    if (node.nodeType === BLOCKS.HEADING_6) {
      const text = getBlockContent(node);
      if (text) {
        acc.push({ color: REBRAND_COLOR.BLACK, text });
      }
      return acc;
    }

    if (node.nodeType === BLOCKS.PARAGRAPH) {
      const text = getBlockContent(node);
      if (text) {
        acc.push({ color: REBRAND_COLOR.BLACK_80, text });
      }
      return acc;
    }

    if (node.nodeType === BLOCKS.UL_LIST) {
      const listItems = (node.content || [])
        .map(getBlockContent)
        .filter(Boolean);

      if (listItems.length > 0) {
        acc.push({
          color: REBRAND_COLOR.BLACK_80,
          text: (
            <ul style={{ margin: '0px', paddingLeft: '18px' }}>
              {listItems.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          )
        });
      }
    }

    return acc;
  }, []);
};

const mapAccordionItem = item => ({
  ...item,
  content: Array.isArray(item?.content)
    ? item.content
    : mapAccordionContent(item?.content)
});

const mapLoanColumn = column => ({
  ...column,
  contentfulID: column?.id,
  benefits: getRichTextListItems(column?.benefits),
  explanation: {
    title: column?.explanationTitle,
    body: getRichTextParagraphContent(column?.explanationBodyText)
  },
  accordionItemsData: column?.accordionItems?.map(mapAccordionItem),
  accordionItemsVariableData: column?.accordionItemsVariable?.map(
    mapAccordionItem
  )
});

const mapFaqItems = faqItems =>
  (faqItems || []).map(item => ({
    label: item?.label,
    href: item?.link,
    title: item?.modalTitle,
    content: item?.modalContent
      ? documentToReactComponents(
          item.modalContent,
          getCdrEntryBindingRichTextOptions(FAQ_RICH_TEXT_CONFIG)
        )
      : undefined
  }));

export const mapProductComparisonFromContentful = item => ({
  ...item,
  contentfulID: item?.id,
  loanColumns: item?.loanColumns?.map(mapLoanColumn),
  faq: item?.faq
    ? {
        ...item.faq,
        faqItems: mapFaqItems(item.faq.faqItems)
      }
    : item?.faq
});
